import { useState } from "react";
import { Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { useProjectCartridges } from "@/hooks/useProjectCartridges";
import { useCartridges } from "@/hooks/useCartridges";
import { assignToProject, unassignFromProject } from "@/api/cartridges";
import { formatRelativeTime } from "@/lib/date";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface ProjectCartridgesSectionProps {
  projectSlug: string;
}

export function ProjectCartridgesSection({
  projectSlug,
}: ProjectCartridgesSectionProps) {
  const { cartridges, loading, error, refetch } = useProjectCartridges(projectSlug);
  const { cartridges: installedCartridges } = useCartridges();

  const [assignOpen, setAssignOpen] = useState(false);
  const [selectedCartridge, setSelectedCartridge] = useState("");
  const [selectedPolicy, setSelectedPolicy] = useState<"required" | "optional">("optional");
  const [assigning, setAssigning] = useState(false);

  const [unassignTarget, setUnassignTarget] = useState<string | null>(null);
  const [unassigning, setUnassigning] = useState(false);

  const assignedNames = new Set(cartridges.map((c) => c.cartridge_name));
  const availableToAssign = installedCartridges.filter(
    (c) => !assignedNames.has(c.cartridge_name),
  );

  async function handleAssign() {
    if (!selectedCartridge) return;
    setAssigning(true);
    try {
      await assignToProject(selectedCartridge, projectSlug, selectedPolicy);
      refetch();
      setAssignOpen(false);
      setSelectedCartridge("");
      setSelectedPolicy("optional");
      toast.success(`${selectedCartridge} assigned`);
    } catch {
      toast.error("Failed to assign cartridge");
    } finally {
      setAssigning(false);
    }
  }

  async function handleUnassign() {
    if (!unassignTarget) return;
    setUnassigning(true);
    try {
      await unassignFromProject(unassignTarget, projectSlug);
      refetch();
      toast.success(`${unassignTarget} unassigned`);
    } catch {
      toast.error("Failed to unassign cartridge");
    } finally {
      setUnassigning(false);
      setUnassignTarget(null);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-4 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>Loading cartridges…</span>
      </div>
    );
  }

  if (error) {
    return <p className="py-4 text-sm text-destructive">{error}</p>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {cartridges.length === 0
            ? "No cartridges assigned to this project."
            : `${cartridges.length} cartridge${cartridges.length === 1 ? "" : "s"} assigned.`}
        </p>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setAssignOpen(true)}
          disabled={availableToAssign.length === 0}
        >
          <Plus className="mr-1 h-4 w-4" />
          Assign Cartridge
        </Button>
      </div>

      {cartridges.length > 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Cartridge</TableHead>
              <TableHead>Policy</TableHead>
              <TableHead>Visibility</TableHead>
              <TableHead>Assigned</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {cartridges.map((c) => (
              <TableRow key={c.cartridge_id}>
                <TableCell className="font-medium">{c.cartridge_name}</TableCell>
                <TableCell>{c.policy}</TableCell>
                <TableCell>{c.visibility}</TableCell>
                <TableCell className="text-muted-foreground">
                  {formatRelativeTime(c.assigned_at)}
                </TableCell>
                <TableCell>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setUnassignTarget(c.cartridge_name)}
                  >
                    Unassign
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* Assign dialog */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Cartridge</DialogTitle>
            <DialogDescription>
              Select a cartridge to assign to this project.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <label className="text-sm font-medium">Cartridge</label>
              <Select value={selectedCartridge} onValueChange={setSelectedCartridge}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a cartridge…" />
                </SelectTrigger>
                <SelectContent>
                  {availableToAssign.map((c) => (
                    <SelectItem key={c.cartridge_name} value={c.cartridge_name}>
                      {c.cartridge_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Policy</label>
              <Select
                value={selectedPolicy}
                onValueChange={(v) => setSelectedPolicy(v as "required" | "optional")}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="optional">optional</SelectItem>
                  <SelectItem value="required">required</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAssignOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => void handleAssign()} disabled={!selectedCartridge || assigning}>
              Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unassign confirmation dialog */}
      <Dialog open={Boolean(unassignTarget)} onOpenChange={() => setUnassignTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Unassign Cartridge</DialogTitle>
            <DialogDescription>
              Remove <strong>{unassignTarget}</strong> from this project? This does not uninstall it from the org.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setUnassignTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => void handleUnassign()}
              disabled={unassigning}
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
